/**
 * useBusinessContext — react-query hook for the user's business context bundle.
 *
 * Reads `v_user_context_bundle` (RLS-scoped to the caller). Used by the UI to
 * render the completeness banner, OnboardingGate redirect logic, and the
 * /onboarding wizard prefill. Does NOT participate in AI prompt construction —
 * that happens server-side in `llm-proxy` (see
 * `supabase/functions/_shared/userContext.ts`).
 *
 * Spec: docs/stories/story-onboarding-1-foundation.md
 */

import { useMemo } from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";
import type { Database } from "@/types/supabase";

export type BusinessContextRow = Database["public"]["Views"]["v_user_context_bundle"]["Row"];

const STALE_MS = 5 * 60 * 1000;

export function businessContextQueryKey(userId: string | null | undefined) {
  return ["user-business-context", userId ?? "anon"] as const;
}

export interface UseBusinessContextResult {
  query: UseQueryResult<BusinessContextRow | null>;
  completenessPct: number;
  isComplete: boolean;
}

export function useBusinessContext(): UseBusinessContextResult {
  const { client, isReady } = useSupabaseClient();
  const { user } = useCurrentUser();
  const { profileId } = useProfileId();
  const userId = user?.id ?? null;

  const query = useQuery<BusinessContextRow | null>({
    queryKey: businessContextQueryKey(profileId ?? userId),
    enabled: isReady && Boolean(profileId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!profileId) return null;
      // Admin RLS grants multi-row access; we MUST filter by user_id explicitly
      // to avoid rendering another user's profile in this user's UI.
      const { data, error } = await client
        .from("v_user_context_bundle")
        .select("*")
        .eq("user_id", profileId)
        .maybeSingle();
      if (error) throw error;
      return (data as BusinessContextRow | null) ?? null;
    },
  });

  return useMemo(() => {
    const pct = query.data?.completeness_pct ?? 0;
    // Mirror onto window so non-React helpers (edgeFnError toast factory)
    // can pick the right CTA target without prop-drilling. Safe to set on
    // every render — it's just a number.
    try {
      (globalThis as unknown as { __synkraOnboardingPct?: number }).__synkraOnboardingPct = pct;
    } catch {
      /* ignore */
    }
    return {
      query,
      completenessPct: pct,
      isComplete: pct >= 100,
    };
  }, [query]);
}

export function useInvalidateBusinessContext(): () => Promise<void> {
  const qc = useQueryClient();
  const { user } = useCurrentUser();
  const { profileId } = useProfileId();
  return async () => {
    await qc.invalidateQueries({
      queryKey: businessContextQueryKey(profileId ?? user?.id),
    });
  };
}
